// App-config fetch — GET /api/config. Surfaces three flags:
//   - useRealRingDeps  — true when SSO/provisioning accessors are real Ring.
//   - useAvaRingDeps   — true when camera/recording accessors talk to AVA
//                        (Ring App Store). Either flag hides
//                        <MockedDepsBanner>.
//   - debugWhoamiEnabled — hides <DebugPanel> (and /api/debug/whoami call)
//     when false.
//
// If the backend returns 404 (endpoint not yet implemented), we fall back
// to the conservative defaults: banner visible, debug panel hidden.

import { api, ApiError } from "./client";

export interface AppConfig {
  useRealRingDeps: boolean;
  useAvaRingDeps: boolean;
  debugWhoamiEnabled: boolean;
}

interface AppConfigWire {
  use_real_ring_deps?: boolean;
  useRealRingDeps?: boolean;
  use_ava_ring_deps?: boolean;
  useAvaRingDeps?: boolean;
  debug?: { whoami?: { enabled?: boolean } };
  debug_whoami_enabled?: boolean;
  debugWhoamiEnabled?: boolean;
}

export const DEFAULT_APP_CONFIG: AppConfig = Object.freeze({
  useRealRingDeps: false,
  useAvaRingDeps: false,
  debugWhoamiEnabled: false,
});

let logged404 = false;

export async function getAppConfig(): Promise<AppConfig> {
  try {
    const raw = await api.get<AppConfigWire>("/api/config", {
      skipActingRole: true,
    });
    const useRealRingDeps =
      raw.useRealRingDeps ?? raw.use_real_ring_deps ?? DEFAULT_APP_CONFIG.useRealRingDeps;
    const useAvaRingDeps =
      raw.useAvaRingDeps ?? raw.use_ava_ring_deps ?? DEFAULT_APP_CONFIG.useAvaRingDeps;
    const debugWhoamiEnabled =
      raw.debugWhoamiEnabled ??
      raw.debug_whoami_enabled ??
      raw.debug?.whoami?.enabled ??
      DEFAULT_APP_CONFIG.debugWhoamiEnabled;
    return { useRealRingDeps, useAvaRingDeps, debugWhoamiEnabled };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      if (!logged404) {
        logged404 = true;
        // eslint-disable-next-line no-console
        console.info(
          "[config] /api/config returned 404; falling back to defaults (banner visible, debug hidden)",
        );
      }
      return { ...DEFAULT_APP_CONFIG };
    }
    throw err;
  }
}
